import { useEffect, useMemo, useState } from "react";
import { motion, ease as mmEase } from "@/lib/motion";
import {
  Users, ShieldCheck, Receipt, TrendingUp, Banknote,
  Check, X, Save, Search, CheckCircle2, Building2,
  ChevronDown, ChevronUp, FileText, MessageSquare, AlertTriangle, Clock, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { CURRENCY_BY_CODE } from "@/lib/constants";
import { formatDate, initials } from "@/lib/format";

const ez = mmEase.expo;

const TABS = [
  { v: "overview",    label: "Overview",     icon: TrendingUp  },
  { v: "users",       label: "Users",        icon: Users       },
  { v: "kyc",         label: "KYC queue",    icon: ShieldCheck },
  { v: "businesses",  label: "Businesses",   icon: Building2   },
  { v: "tx",          label: "Transactions", icon: Receipt     },
  { v: "fx",          label: "FX rates",     icon: Banknote    },
];

const KYC_STATUS_CLS = {
  approved:   "badge-emerald",
  pending:    "badge-gold",
  rejected:   "badge-red",
  unverified: "badge-navy",
};

const AVATAR_COLORS = ["#10B981", "#7C3AED", "#06B6D4", "#F59E0B", "#EF4444", "#0B1D3A"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border/60 rounded-2xl px-3.5 py-2.5 shadow-lg text-[12px]">
      <p className="text-muted-foreground font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="font-bold num-tabular" style={{ color: p.color || "#10B981" }}>
          {p.name}: {p.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Admin console</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">
          MoneyMeet operations
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Oversee users, KYC, transactions, and FX. Every lever, one command center.
        </p>
      </motion.div>

      {/* ── Tab bar ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: ez, delay: 0.05 }}
        className="flex gap-1 p-1 bg-secondary/70 rounded-2xl self-start w-fit flex-wrap"
      >
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`admin-tab-${t.v}`}
            onClick={() => setTab(t.v)}
            data-active={tab === t.v ? "true" : "false"}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              tab === t.v
                ? "bg-emerald text-white shadow-[0_2px_12px_-2px_rgba(16,185,129,0.45)]"
                : "text-muted-foreground hover:text-navy"
            }`}
          >
            <t.icon size={13} strokeWidth={1.8} />
            {t.label}
          </button>
        ))}
      </motion.div>

      {tab === "overview"   && <Overview />}
      {tab === "users"      && <UsersTab />}
      {tab === "kyc"        && <KycQueue />}
      {tab === "businesses" && <BusinessQueue />}
      {tab === "tx"         && <TxTab />}
      {tab === "fx"         && <FxTab />}
    </div>
  );
}

/* ── OVERVIEW ───────────────────────────────────────────────────── */
function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)); }, []);

  if (!data) return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-28 rounded-3xl" />)}
    </div>
  );

  const stats = [
    { label: "Total users",   value: data.total_users,           icon: Users,       accent: "#10B981" },
    { label: "Pending KYC",   value: data.pending_kyc,           icon: ShieldCheck, accent: "#F59E0B" },
    { label: "Transactions",  value: data.total_transactions,    icon: Receipt,     accent: "#06B6D4" },
    { label: "Completed",     value: data.completed_transactions, icon: CheckCircle2,accent: "#10B981" },
  ];

  const daily  = data.daily.map((d) => ({
    name: d._id?.slice(5) || "—", count: d.count, amount: Number(d.amount.toFixed(2)),
  }));
  const byCurr = data.by_currency.map((c) => ({
    name: c._id,
    total: Number(c.total.toFixed(2)),
    fees:  Number(c.fees.toFixed(2)),
    color: CURRENCY_BY_CODE[c._id]?.color || "#10B981",
  }));

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            className="card-float p-5 relative overflow-hidden group"
            initial={{ opacity: 0, y: 14, scale: 0.95 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            transition={{ duration: 0.4, ease: ez, delay: i * 0.06 }}
            whileHover={{ y: -3 }}
          >
            <div
              className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-[0.12] group-hover:opacity-[0.2] transition-opacity duration-500 pointer-events-none"
              style={{ background: s.accent }}
            />
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{s.label}</p>
                <p className="font-display text-3xl font-black text-navy num-tabular mt-1">
                  {s.value?.toLocaleString()}
                </p>
              </div>
              <div
                className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                style={{ background: `${s.accent}14`, color: s.accent }}
              >
                <s.icon size={17} strokeWidth={1.8} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card-elevated p-5">
          <h3 className="font-bold text-navy text-sm mb-0.5">Daily transfer volume</h3>
          <p className="text-[11px] text-muted-foreground mb-4">Last 30 days</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="adminEmerald" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#10B981" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,29,58,0.05)" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="count" name="Transactions" stroke="#10B981" strokeWidth={2.5} fill="url(#adminEmerald)" dot={false} activeDot={{ r: 4, fill: "#10B981" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-elevated p-5">
          <h3 className="font-bold text-navy text-sm mb-0.5">Volume by source currency</h3>
          <p className="text-[11px] text-muted-foreground mb-4">Cumulative USD equivalent</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byCurr} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,29,58,0.05)" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" name="Total" radius={[7, 7, 0, 0]}>
                  {byCurr.map((c, i) => <Cell key={i} fill={c.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Revenue by corridor */}
      <div className="card-elevated p-5">
        <h3 className="font-bold text-navy text-sm mb-4">Revenue (fees) by corridor</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {byCurr.map((c) => (
            <div
              key={c.name}
              className="rounded-2xl p-4 relative overflow-hidden"
              style={{ background: `${c.color}0E` }}
            >
              <div
                className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-[0.15] pointer-events-none"
                style={{ background: c.color }}
              />
              <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground font-bold mb-1 relative">
                {c.name} fees
              </p>
              <p className="font-display text-xl font-black num-tabular relative" style={{ color: c.color }}>
                {c.fees.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── USERS ──────────────────────────────────────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q,     setQ]     = useState("");

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => users.filter((u) => !q || (u.email + u.full_name).toLowerCase().includes((q || "").toLowerCase())),
    [users, q]
  );

  const toggleSuspend = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { suspended: !u.suspended });
    toast.success(`${u.full_name} ${!u.suspended ? "suspended" : "reinstated"}`);
    load();
  };

  return (
    <div className="space-y-4">
      <label
        className="flex items-center gap-2 px-3.5 py-2.5 bg-white border border-border/70 rounded-2xl max-w-sm focus-within:border-emerald focus-within:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all"
        htmlFor="admin-search"
      >
        <Search size={14} className="text-muted-foreground shrink-0" />
        <input
          id="admin-search"
          data-testid="admin-users-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users…"
          className="bg-transparent outline-none text-sm flex-1 text-navy placeholder:text-muted-foreground"
        />
      </label>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-border/50 bg-secondary/40">
              {["User", "Country", "Role", "KYC", "Joined", ""].map((h, i) => (
                <th
                  key={i}
                  className={`text-[9px] uppercase tracking-[0.16em] font-bold text-muted-foreground py-3 px-4 ${
                    i > 3 ? "text-right" : "text-left"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {filtered.map((u, i) => (
              <tr
                key={u.id}
                data-testid={`admin-user-row-${u.id}`}
                className="hover:bg-secondary/30 transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-xl grid place-items-center text-white font-black text-[11px] shrink-0"
                      style={{ background: u.avatar_color || AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                    >
                      {initials(u.full_name)}
                    </div>
                    <div>
                      <p className="font-semibold text-navy text-[13px]">{u.full_name}</p>
                      <p className="text-[11px] text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-[13px] text-navy">{u.country || "—"}</td>
                <td className="px-4 py-3">
                  <span className={u.role === "admin" ? "badge-gold" : "badge-navy"}>{u.role}</span>
                </td>
                <td className="px-4 py-3 capitalize">
                  <span className={KYC_STATUS_CLS[u.kyc_status] || "badge-navy"}>{u.kyc_status}</span>
                </td>
                <td className="px-4 py-3 text-[11px] text-muted-foreground">{formatDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "admin" && (
                    <button
                      data-testid={`admin-user-suspend-${u.id}`}
                      onClick={() => toggleSuspend(u)}
                      className={`text-[11px] font-bold px-3 py-1.5 rounded-full transition-all ${
                        u.suspended
                          ? "bg-emerald/10 text-emerald hover:bg-emerald hover:text-white"
                          : "bg-red-50 text-red-600 hover:bg-red-500 hover:text-white"
                      }`}
                    >
                      {u.suspended ? "Reinstate" : "Suspend"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── KYC QUEUE ──────────────────────────────────────────────────── */
function KycQueue() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/kyc/pending")
      .then(({ data }) => setPending(data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const review = async (uid, status) => {
    await api.post(`/admin/kyc/${uid}/review`, { status });
    toast.success(`KYC ${status}`);
    load();
  };

  if (!loading && pending.length === 0) return (
    <div className="card-elevated py-16 text-center">
      <div className="w-14 h-14 rounded-2xl bg-emerald/10 grid place-items-center mx-auto text-emerald mb-4">
        <CheckCircle2 size={22} strokeWidth={1.8} />
      </div>
      <p className="font-bold text-navy">Inbox zero</p>
      <p className="text-sm text-muted-foreground mt-1">Nothing to review right now.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {pending.map((u, i) => (
        <motion.div
          key={u.id}
          data-testid={`admin-kyc-row-${u.id}`}
          className="card-float p-5"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.36, ease: ez, delay: i * 0.05 }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-2xl grid place-items-center text-white font-black text-sm shrink-0"
                style={{ background: u.avatar_color || AVATAR_COLORS[i % AVATAR_COLORS.length] }}
              >
                {initials(u.full_name)}
              </div>
              <div>
                <p className="font-semibold text-navy">{u.full_name}</p>
                <p className="text-[11px] text-muted-foreground">{u.email} · {u.country}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={`admin-kyc-approve-${u.id}`}
                onClick={() => review(u.id, "approved")}
                className="btn-primary text-sm py-2 px-4"
              >
                <Check size={13} /> Approve
              </button>
              <button
                data-testid={`admin-kyc-reject-${u.id}`}
                onClick={() => review(u.id, "rejected")}
                className="inline-flex items-center gap-1.5 text-sm font-bold bg-red-50 text-red-600 hover:bg-red-500 hover:text-white px-4 py-2 rounded-full transition-all"
              >
                <X size={13} /> Reject
              </button>
            </div>
          </div>

          {(u.documents || []).length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {u.documents.map((d) => (
                <div
                  key={d.id}
                  className="bg-emerald/[0.06] border border-emerald/20 rounded-xl px-3 py-2 flex items-center gap-2"
                >
                  <CheckCircle2 size={11} className="text-emerald shrink-0" />
                  <span className="text-[11px] font-semibold text-navy capitalize">
                    {d.doc_type?.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
}

/* ── BUSINESS ACCOUNTS ───────────────────────────────────────────── */
const BIZ_STATUS_CLS = {
  approved:     "badge-emerald",
  pending:      "badge-gold",
  under_review: "badge-gold",
  rejected:     "badge-red",
};

const RISK_OPTS = ["low", "medium", "high", "critical"];
const RISK_CLS  = { low: "badge-emerald", medium: "badge-gold", high: "badge-red", critical: "badge-red" };

function BusinessDetailPanel({ uid, onClose }) {
  const [detail,   setDetail]   = useState(null);
  const [docs,     setDocs]     = useState([]);
  const [notes,    setNotes]    = useState([]);
  const [audit,    setAudit]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [risk,     setRisk]     = useState("");
  const [saving,   setSaving]   = useState(false);
  const [section,  setSection]  = useState("overview");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/businesses/${uid}/detail`),
      api.get(`/admin/businesses/${uid}/documents`),
      api.get(`/admin/businesses/${uid}/notes`),
      api.get(`/admin/businesses/${uid}/audit`),
    ]).then(([d, dc, n, a]) => {
      setDetail(d.data);
      setDocs(dc.data);
      setNotes(n.data);
      setAudit(a.data);
      setRisk(d.data?.business_profile?.risk_level || "low");
    }).catch(() => toast.error("Could not load business detail"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/businesses/${uid}/notes`, { note: noteText, note_type: noteType });
      toast.success("Note added");
      setNoteText("");
      const { data } = await api.get(`/admin/businesses/${uid}/notes`);
      setNotes(data);
    } catch { toast.error("Could not add note"); }
    finally { setSaving(false); }
  };

  const saveRisk = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/businesses/${uid}/risk`, { risk_level: risk });
      toast.success(`Risk level set to ${risk}`);
    } catch { toast.error("Could not update risk level"); }
    finally { setSaving(false); }
  };

  const triggerReview = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/businesses/${uid}/review`);
      toast.success("Business moved to under review");
      load();
    } catch { toast.error("Could not trigger review"); }
    finally { setSaving(false); }
  };

  const biz = detail?.business_profile || {};
  const user = detail?.user || {};

  const SECTIONS = [
    { v: "overview",  label: "Overview" },
    { v: "documents", label: `Documents (${docs.length})` },
    { v: "notes",     label: `Notes (${notes.length})` },
    { v: "audit",     label: "Audit log" },
  ];

  return (
    <div className="mt-4 border-t border-border/60 pt-4 space-y-4" data-testid={`admin-biz-detail-${uid}`}>
      {/* Sub-tabs */}
      <div className="flex gap-1 flex-wrap">
        {SECTIONS.map((s) => (
          <button
            key={s.v}
            onClick={() => setSection(s.v)}
            className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-all ${
              section === s.v ? "bg-navy text-white" : "bg-secondary text-muted-foreground hover:text-navy"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="skeleton h-10 rounded-xl" />)}</div>
      ) : section === "overview" ? (
        <div className="space-y-4">
          {/* Full detail grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
            {[
              ["Business name",    biz.business_name],
              ["Business type",    biz.business_type?.replace(/_/g," ")],
              ["Reg number",       biz.registration_number],
              ["Tax number",       biz.tax_number],
              ["Address",          biz.business_address],
              ["Website",          biz.website],
              ["User email",       user.email],
              ["Country",          user.country],
              ["KYC status",       user.kyc_status],
              ["Account type",     user.account_type],
            ].filter(([,v]) => v).map(([label, value]) => (
              <div key={label} className="bg-secondary/50 rounded-xl px-3 py-2">
                <p className="text-muted-foreground uppercase tracking-wider text-[9px] font-bold mb-0.5">{label}</p>
                <p className="font-semibold text-navy truncate capitalize">{value}</p>
              </div>
            ))}
          </div>

          {/* Risk level */}
          <div className="flex items-center gap-3 flex-wrap p-3 bg-secondary/40 rounded-2xl">
            <div className="flex items-center gap-2 shrink-0">
              <AlertTriangle size={13} className="text-amber-500" />
              <p className="text-[12px] font-bold text-navy">Risk level</p>
              <span className={RISK_CLS[risk] || "badge-navy"}>{risk}</span>
            </div>
            <select
              value={risk}
              onChange={(e) => setRisk(e.target.value)}
              className="input-field text-xs py-1.5 flex-1 min-w-[120px]"
              data-testid={`admin-biz-risk-${uid}`}
            >
              {RISK_OPTS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              onClick={saveRisk}
              disabled={saving}
              className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50"
              data-testid={`admin-biz-risk-save-${uid}`}
            >
              <Save size={11} /> Save
            </button>
            <button
              onClick={triggerReview}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-navy/10 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-full transition-all disabled:opacity-50"
              data-testid={`admin-biz-review-${uid}`}
            >
              <Clock size={11} /> Set under review
            </button>
          </div>

          {/* Wallets summary */}
          {(detail?.wallets || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Wallets</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {detail.wallets.map((w) => (
                  <div key={w.id} className="bg-emerald/[0.06] border border-emerald/20 rounded-xl px-3 py-2 text-[11px]">
                    <p className="text-muted-foreground uppercase tracking-wider text-[9px] font-bold">{w.currency}</p>
                    <p className="font-black text-navy tabular-nums">{w.balance?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : section === "documents" ? (
        <div className="space-y-2">
          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No documents uploaded yet.</p>
          ) : docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 bg-secondary/40 rounded-xl" data-testid={`admin-biz-doc-${d.id}`}>
              <FileText size={14} className="text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-navy capitalize">{d.doc_type?.replace(/_/g," ")}</p>
                <p className="text-[10px] text-muted-foreground">{new Date(d.created_at).toLocaleDateString("en-GB")}</p>
              </div>
              <span className={BIZ_STATUS_CLS[d.status] || "badge-navy"}>{d.status || "pending"}</span>
            </div>
          ))}
        </div>
      ) : section === "notes" ? (
        <div className="space-y-3">
          {/* Add note */}
          <div className="space-y-2 p-3 bg-secondary/40 rounded-2xl">
            <p className="text-[11px] font-bold text-navy">Add compliance note</p>
            <div className="flex gap-2 flex-wrap">
              <select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
                className="input-field text-xs py-1.5 w-36 shrink-0"
              >
                {["general","compliance","risk","approval","rejection"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Internal compliance note…"
                rows={2}
                className="input-field resize-none flex-1 min-w-[200px] text-xs"
                data-testid={`admin-biz-note-input-${uid}`}
              />
            </div>
            <button
              onClick={addNote}
              disabled={saving || !noteText.trim()}
              className="btn-primary text-xs py-1.5 px-4 disabled:opacity-50"
              data-testid={`admin-biz-note-save-${uid}`}
            >
              <MessageSquare size={11} /> Add note
            </button>
          </div>
          {/* Existing notes */}
          {notes.map((n, i) => (
            <div key={i} className="p-3 bg-secondary/30 rounded-xl border-l-2 border-navy/30" data-testid={`admin-biz-note-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="badge-navy text-[9px]">{n.note_type || "general"}</span>
                <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                {n.added_by && <span className="text-[10px] text-muted-foreground">· {n.added_by}</span>}
              </div>
              <p className="text-[12px] text-navy">{n.note}</p>
            </div>
          ))}
          {notes.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No notes yet.</p>}
        </div>
      ) : section === "audit" ? (
        <div className="space-y-2">
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No audit entries found.</p>
          ) : audit.map((a, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-xl" data-testid={`admin-biz-audit-${i}`}>
              <Clock size={12} className="text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-navy">{a.event || a.message}</p>
                {a.detail && <p className="text-[10px] text-muted-foreground mt-0.5">{a.detail}</p>}
              </div>
              <p className="text-[10px] text-muted-foreground shrink-0">{new Date(a.at || a.created_at).toLocaleDateString("en-GB", { day:"numeric", month:"short" })}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BusinessQueue() {
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [filter, setFilter] = useState("pending");
  const [expanded, setExpanded] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { uid, name }
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    setLoad(true);
    const endpoint = filter === "all" ? "/admin/businesses" : "/admin/businesses/pending";
    api.get(endpoint)
      .then(({ data }) => setItems(data))
      .finally(() => setLoad(false));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (uid, name) => {
    await api.post(`/admin/businesses/${uid}/approve`);
    toast.success(`${name} approved`);
    load();
  };

  const openReject = (uid, name) => { setRejectModal({ uid, name }); setRejectReason(""); };
  const confirmReject = async () => {
    if (!rejectModal) return;
    await api.post(`/admin/businesses/${rejectModal.uid}/reject`, { reason: rejectReason || undefined });
    toast.success(`${rejectModal.name} rejected`);
    setRejectModal(null);
    load();
  };

  const toggleExpand = (uid) => setExpanded((prev) => prev === uid ? null : uid);

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-2">
        {[["pending", "Pending review"], ["all", "All businesses"]].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
              filter === v ? "bg-emerald text-white" : "bg-secondary text-muted-foreground hover:text-navy"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading && (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="skeleton h-28 rounded-3xl" />)}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="card-elevated py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-emerald/10 grid place-items-center mx-auto text-emerald mb-4">
            <CheckCircle2 size={22} strokeWidth={1.8} />
          </div>
          <p className="font-bold text-navy">All clear</p>
          <p className="text-sm text-muted-foreground mt-1">No businesses awaiting review.</p>
        </div>
      )}

      {!loading && items.map((u, i) => {
        const biz = u.business_profile || {};
        const isExpanded = expanded === u.id;
        return (
          <motion.div
            key={u.id}
            data-testid={`admin-biz-row-${u.id}`}
            className="card-float p-5"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.36, ease: ez, delay: i * 0.05 }}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-2xl grid place-items-center text-white font-black text-sm shrink-0"
                  style={{ background: u.avatar_color || AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                >
                  <Building2 size={18} />
                </div>
                <div>
                  <p className="font-bold text-navy">{biz.business_name || u.full_name}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{biz.business_type?.replace(/_/g, " ")} · {u.email}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className={BIZ_STATUS_CLS[biz.verification_status] || "badge-navy"}>
                      {biz.verification_status || "pending"}
                    </span>
                    {biz.risk_level && biz.risk_level !== "low" && (
                      <span className={RISK_CLS[biz.risk_level] || "badge-navy"}>
                        risk: {biz.risk_level}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  data-testid={`admin-biz-approve-${u.id}`}
                  onClick={() => approve(u.id, biz.business_name || u.full_name)}
                  className="btn-primary text-sm py-2 px-4"
                >
                  <Check size={13} /> Approve
                </button>
                <button
                  data-testid={`admin-biz-reject-${u.id}`}
                  onClick={() => openReject(u.id, biz.business_name || u.full_name)}
                  className="inline-flex items-center gap-1.5 text-sm font-bold bg-red-50 text-red-600 hover:bg-red-500 hover:text-white px-4 py-2 rounded-full transition-all"
                >
                  <X size={13} /> Reject
                </button>
                <button
                  onClick={() => toggleExpand(u.id)}
                  className="w-8 h-8 grid place-items-center rounded-xl bg-secondary/60 text-muted-foreground hover:text-navy transition-all"
                  aria-label={isExpanded ? "Collapse detail" : "Expand detail"}
                  data-testid={`admin-biz-expand-${u.id}`}
                >
                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>
            </div>

            {/* Summary detail grid */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
              {[
                ["Reg number", biz.registration_number],
                ["Tax number", biz.tax_number],
                ["Country", u.country],
                ["KYC", u.kyc_status],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} className="bg-secondary/60 rounded-xl px-3 py-2">
                  <p className="text-muted-foreground font-semibold uppercase tracking-wider text-[9px] mb-0.5">{label}</p>
                  <p className="font-bold text-navy capitalize">{value}</p>
                </div>
              ))}
            </div>

            {biz.business_address && (
              <p className="mt-2 text-[11px] text-muted-foreground pl-1">
                📍 {biz.business_address}
              </p>
            )}

            {/* Expandable detail panel */}
            {isExpanded && <BusinessDetailPanel uid={u.id} onClose={() => setExpanded(null)} />}
          </motion.div>
        );
      })}

      {/* Reject reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setRejectModal(null)}>
          <div
            className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-navy text-lg mb-1">Reject {rejectModal.name}?</h3>
            <p className="text-sm text-muted-foreground mb-4">Provide an optional reason — it will be sent to the business.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Missing registration documents…"
              rows={3}
              className="input-field resize-none w-full mb-4"
            />
            <div className="flex gap-2">
              <button onClick={() => setRejectModal(null)} className="flex-1 py-2.5 text-sm font-bold border border-border/70 rounded-full hover:bg-secondary/40 transition-colors">
                Cancel
              </button>
              <button onClick={confirmReject} className="flex-1 py-2.5 text-sm font-bold bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors">
                Confirm rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── TRANSACTIONS ────────────────────────────────────────────────── */
function TxTab() {
  const [txs, setTxs] = useState([]);
  useEffect(() => {
    api.get("/admin/transactions", { params: { limit: 500 } }).then(({ data }) => setTxs(data));
  }, []);

  return (
    <div className="card-elevated overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead>
          <tr className="border-b border-border/50 bg-secondary/40">
            {["Reference", "From", "To", "Amount", "Fee", "Status", "Date"].map((h, i) => (
              <th
                key={i}
                className={`text-[9px] uppercase tracking-[0.16em] font-bold text-muted-foreground py-3 px-4 ${
                  i >= 3 && i < 5 ? "text-right" : "text-left"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {txs.map((t) => (
            <tr
              key={t.id}
              data-testid={`admin-tx-row-${t.id}`}
              className="hover:bg-secondary/30 transition-colors"
            >
              <td className="px-4 py-3 font-mono text-[11px] text-navy/80">{t.reference}</td>
              <td className="px-4 py-3 text-[13px]">{CURRENCY_BY_CODE[t.from_currency]?.flag} {t.from_currency}</td>
              <td className="px-4 py-3 text-[13px]">{CURRENCY_BY_CODE[t.to_currency]?.flag} {t.to_currency}</td>
              <td className="px-4 py-3 text-right font-bold num-tabular text-navy">{t.send_amount}</td>
              <td className="px-4 py-3 text-right num-tabular text-muted-foreground">{t.fee}</td>
              <td className="px-4 py-3">
                <span className={
                  t.status === "completed" ? "badge-emerald" :
                  t.status === "failed"    ? "badge-red"     :
                  "badge-gold"
                }>
                  {t.status}
                </span>
              </td>
              <td className="px-4 py-3 text-[11px] text-muted-foreground">{formatDate(t.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── FX RATES ────────────────────────────────────────────────────── */
function FxTab() {
  const [rates, setRates] = useState([]);
  const load = () => api.get("/admin/fx/rates").then(({ data }) => setRates(data));
  useEffect(() => { load(); }, []);

  const update = async (r, value) => {
    const rate = Number(value);
    if (!rate || rate <= 0) return;
    await api.post("/admin/fx/rates", { base: r.base, target: r.target, rate });
    toast.success(`${r.base}→${r.target} rate updated`);
    load();
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-bold text-navy">FX rates</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          All rates expressed as USD → target. Edit and press Save to update live.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rates.map((r, i) => {
          const c = CURRENCY_BY_CODE[r.target];
          const color = c?.color || AVATAR_COLORS[i % AVATAR_COLORS.length];
          return (
            <div
              key={`${r.base}-${r.target}`}
              className="card-float p-4 relative overflow-hidden group"
            >
              <div
                className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-[0.10] group-hover:opacity-[0.18] transition-opacity duration-500 pointer-events-none"
                style={{ background: color }}
              />
              <div className="relative flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{c?.flag}</span>
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
                      {r.base} → {r.target}
                    </p>
                    <p className="font-semibold text-navy text-sm">{c?.name}</p>
                  </div>
                </div>
                <RateInput rate={r.rate} onSave={(v) => update(r, v)} testid={`admin-fx-${r.target}`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RateInput({ rate, onSave, testid }) {
  const [v, setV] = useState(rate);
  useEffect(() => setV(rate), [rate]);
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        step="0.0001"
        value={v}
        onChange={(e) => setV(e.target.value)}
        data-testid={`${testid}-input`}
        className="w-28 input-field text-right font-mono text-sm px-3 py-2"
      />
      <button
        data-testid={`${testid}-save`}
        onClick={() => onSave(v)}
        className="w-9 h-9 grid place-items-center bg-emerald text-white hover:bg-emerald-hover rounded-xl transition-colors"
        aria-label="Save rate"
      >
        <Save size={13} />
      </button>
    </div>
  );
}
